import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { productService } from "../services/product";
import BoxTypeSelect from "../components/BoxTypeSelect";

const CreateProducts = () => {
  const navigate = useNavigate();
  const [boxTypeOptions, setBoxTypeOptions] = useState([]);
  const [productData, setProductData] = useState({
    productCode: "",
    name: "",
    description: "",
    // category removido conforme solicitação
    isCured: false,
    curingTimeDays: 0,
    initialWeightKg: 0,
    curedWeightKg: 0,
    minStockQuantity: 0,
    maxStockQuantity: 0,
    initialStockQuantity: 0, // Adicionado para o stock inicial
    advancedDetails: "{}", // JSON string
    imageLink: "", // Campo para URL da imagem
    boxTypeDpd: "", // Tipo de caixa para clientes DPD
    boxTypeOx: "", // Tipo de caixa para clientes OX
    validadeDias: 365, // Campo para validade em dias
    isPack: false, // Novo campo: indica se é um pack
    unitsPerPack: 1, // Novo campo: quantidade de unidades por pack
    isActive: true, // Novo campo: produto ativo
    produtividade: 20, // Novo campo: produtividade em % (padrão 20%)
    forAllClients: true, // Novo campo: produto para todos os clientes (padrão: true)
    soldBy: "unit", // unit = por unidade, kg = por peso
  });
  const [showAdvancedDetails, setShowAdvancedDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    productService
      .getBoxTypes()
      .then(setBoxTypeOptions)
      .catch(() => setBoxTypeOptions([]));
  }, []);

  const handleBoxTypeDpdChange = (value) => {
    setProductData((prevData) => ({ ...prevData, boxTypeDpd: value }));
  };

  const handleBoxTypeOxChange = (value) => {
    setProductData((prevData) => ({ ...prevData, boxTypeOx: value }));
  };

  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    setProductData((prevData) => ({
      ...prevData,
      [id]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAdvancedDetailsChange = (e) => {
    setProductData((prevData) => ({
      ...prevData,
      advancedDetails: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Basic validation
      if (!productData.name || !productData.productCode) {
        throw new Error(
          "Nome do produto e Código do Produto são obrigatórios."
        );
      }

      // Parse advancedDetails JSON
      let parsedAdvancedDetails = null;
      if (showAdvancedDetails && productData.advancedDetails) {
        try {
          parsedAdvancedDetails = JSON.parse(productData.advancedDetails);
        } catch (jsonError) {
          throw new Error("Formato JSON inválido para Detalhes Avançados.");
        }
      }

      const productToCreate = {
        product_code: productData.productCode,
        name: productData.name,
        description: productData.description,
        // category removido
        is_cured: productData.isCured,
        curing_time_days: productData.isCured
          ? parseInt(productData.curingTimeDays)
          : null,
        initial_weight_kg: Number(productData.initialWeightKg),
        cured_weight_kg: productData.isCured
          ? Number(productData.curedWeightKg)
          : null,
        min_stock_quantity: Number(productData.minStockQuantity),
        max_stock_quantity: Number(productData.maxStockQuantity),
        initial_stock_quantity: Number(productData.initialStockQuantity), // Enviando para a API
        advanced_details: parsedAdvancedDetails,
        image_url: productData.imageLink || null, // Enviando URL da imagem
        box_type_dpd: productData.boxTypeDpd || null, // Tipo de caixa para clientes DPD
        box_type_ox: productData.boxTypeOx || null, // Tipo de caixa para clientes OX
        validade_dias: Number(productData.validadeDias) || 365, // Enviando validade em dias
        is_pack: productData.isPack, // Novo campo
        units_per_pack: productData.isPack ? Number(productData.unitsPerPack) : 1, // Novo campo
        is_active: productData.isActive, // Novo campo
        produtividade: Number(productData.produtividade) || 20, // Novo campo
        for_all_clients: productData.forAllClients, // Produto para todos os clientes
        sold_by: productData.soldBy || "unit",
      };

      await productService.createProduct(productToCreate);
      setSuccess("Produto criado com sucesso!");
      // Optionally clear form or navigate
      setProductData({
        productCode: "",
        name: "",
        description: "",
        // category removido
        isCured: false,
        curingTimeDays: 0,
        initialWeightKg: 0,
        curedWeightKg: 0,
        minStockQuantity: 0,
        maxStockQuantity: 0,
        initialStockQuantity: 0,
        advancedDetails: "{}",
        imageLink: "",
        boxTypeDpd: "",
        boxTypeOx: "",
        validadeDias: 365,
        isPack: false,
        unitsPerPack: 1,
        isActive: true,
        produtividade: 20,
        forAllClients: true,
        soldBy: "unit",
      });
      setShowAdvancedDetails(false);
      navigate("/stock"); // Uncomment to navigate after creation
    } catch (err) {
      setError(err.message || "Erro ao criar produto.");
      console.error("Erro ao criar produto:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid p-4 sm:p-6 lg:p-8">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate(-1)}
          className="mr-4 text-neutral-600 hover:text-neutral-900"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="text-xl" />
        </button>
        <h1 className="text-2xl">Criar Produto</h1>
      </div>

      {error && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
          role="alert"
        >
          {error}
        </div>
      )}
      {success && (
        <div
          className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4"
          role="alert"
        >
          {success}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-lg p-6"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Image Link - Not in DB schema, keeping for UI but won't be sent */}
            <div>
              <label
                htmlFor="imageLink"
                className="block text-sm text-neutral-700 mb-2"
              >
                Link da Imagem
              </label>
              <input
                type="url"
                id="imageLink"
                placeholder="https://"
                className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                value={productData.imageLink}
                onChange={handleChange}
              />
            </div>

            <div>
              <label
                htmlFor="name"
                className="block text-sm text-neutral-700 mb-2"
              >
                Nome do Produto
              </label>
              <input
                type="text"
                id="name"
                value={productData.name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                required
              />
            </div>

            <div>
              <label
                htmlFor="productCode"
                className="block text-sm text-neutral-700 mb-2"
              >
                Código do Produto
              </label>
              <input
                type="text"
                id="productCode"
                value={productData.productCode}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                required
              />
            </div>

            <div>
              <label
                htmlFor="description"
                className="block text-sm text-neutral-700 mb-2"
              >
                Descrição
              </label>
              <textarea
                id="description"
                rows="4"
                value={productData.description}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
              ></textarea>
            </div>

            <BoxTypeSelect
              id="boxTypeDpd"
              label="Tipo de Caixa DPD"
              value={productData.boxTypeDpd}
              onChange={handleBoxTypeDpdChange}
              options={boxTypeOptions}
            />

            <BoxTypeSelect
              id="boxTypeOx"
              label="Tipo de Caixa OX"
              value={productData.boxTypeOx}
              onChange={handleBoxTypeOxChange}
              options={boxTypeOptions}
            />


            <div>
              <label
                htmlFor="validadeDias"
                className="block text-sm text-neutral-700 mb-2"
              >
                Validade (dias)
              </label>
              <input
                type="number"
                id="validadeDias"
                value={productData.validadeDias}
                onChange={handleChange}
                min="1"
                max="3650"
                className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                placeholder="365"
              />
            </div>

            <div>
              <label
                htmlFor="produtividade"
                className="block text-sm text-neutral-700 mb-2"
              >
                Produtividade (%)
              </label>
              <input
                type="number"
                id="produtividade"
                value={productData.produtividade}
                onChange={handleChange}
                min="1"
                max="100"
                step="0.01"
                className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                placeholder="20"
              />
              <p className="text-xs text-neutral-500 mt-1">
                Exemplo: 20% = 20kg de queijo para 100L de leite
              </p>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            <div>
              <label
                htmlFor="initialWeightKg"
                className="block text-sm text-neutral-700 mb-2"
              >
                Peso por unidade (kg)
              </label>
              <input
                type="number"
                id="initialWeightKg"
                value={productData.initialWeightKg}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                step="0.01"
              />
            </div>

            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="isCured"
                checked={productData.isCured}
                onChange={handleChange}
                className="h-4 w-4 text-neutral-900 rounded border-neutral-300"
              />
              <label
                htmlFor="isCured"
                className="ml-2 text-sm text-neutral-700"
              >
                Queijo com Cura
              </label>
            </div>

            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="isPack"
                checked={productData.isPack}
                onChange={handleChange}
                className="h-4 w-4 text-neutral-900 rounded border-neutral-300"
              />
              <label
                htmlFor="isPack"
                className="ml-2 text-sm text-neutral-700"
              >
                É um Pack
              </label>
            </div>

            {productData.isPack && (
              <div className="mb-6">
                <label
                  htmlFor="unitsPerPack"
                  className="block text-sm text-neutral-700 mb-2"
                >
                  Quantas unidades tem no pack?
                </label>
                <input
                  type="number"
                  id="unitsPerPack"
                  value={productData.unitsPerPack}
                  onChange={handleChange}
                  min="1"
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                  required
                />
              </div>
            )}

            <div className="mb-6">
              <label
                htmlFor="soldBy"
                className="block text-sm text-neutral-700 mb-2"
              >
                Vendido por
              </label>
              <select
                id="soldBy"
                value={productData.soldBy}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
              >
                <option value="unit">Unidade (dividir por unidade)</option>
                <option value="kg">Peso em kg (dividir por kg)</option>
              </select>
            </div>

            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="isActive"
                checked={productData.isActive}
                onChange={handleChange}
                className="h-4 w-4 text-neutral-900 rounded border-neutral-300"
              />
              <label
                htmlFor="isActive"
                className="ml-2 text-sm text-neutral-700"
              >
                Produto Ativo
              </label>
            </div>

            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="forAllClients"
                checked={productData.forAllClients}
                onChange={handleChange}
                className="h-4 w-4 text-neutral-900 rounded border-neutral-300"
              />
              <label
                htmlFor="forAllClients"
                className="ml-2 text-sm text-neutral-700"
              >
                Produto para todos os clientes
              </label>
            </div>
            {!productData.forAllClients && (
              <p className="text-xs text-amber-600 mb-4 -mt-2 ml-6">
                Este produto precisará ser ativado individualmente para cada cliente na tela de detalhes do cliente.
              </p>
            )}

            {productData.isCured && (
              <div id="cureFields" className="space-y-6">
                <div>
                  <label
                    htmlFor="curedWeightKg"
                    className="block text-sm text-neutral-700 mb-2"
                  >
                    Peso Curado (kg)
                  </label>
                  <input
                    type="number"
                    id="curedWeightKg"
                    value={productData.curedWeightKg}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                    step="0.01"
                  />
                </div>
                <div>
                  <label
                    htmlFor="curingTimeDays"
                    className="block text-sm text-neutral-700 mb-2"
                  >
                    Tempo de Cura (dias)
                  </label>
                  <input
                    type="number"
                    id="curingTimeDays"
                    value={productData.curingTimeDays}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                  />
                </div>
              </div>
            )}

            {/* Stock Quantity - This field is not in the DB schema for products, it's for stock entries */}
            {/* <div>
                <label htmlFor="stockQuantity" className="block text-sm text-neutral-700 mb-2">
                  Quantidade em Stock
                </label>
                <input
                  type="number"
                  id="stockQuantity"
                  value={productData.stockQuantity}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                />
              </div> */}

            <div>
              <label
                htmlFor="minStockQuantity"
                className="block text-sm text-neutral-700 mb-2"
              >
                Stock Mínimo
              </label>
              <input
                type="number"
                id="minStockQuantity"
                value={productData.minStockQuantity}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                step="0.01"
              />
            </div>

            <div>
              <label
                htmlFor="maxStockQuantity"
                className="block text-sm text-neutral-700 mb-2"
              >
                Stock Máximo
              </label>
              <input
                type="number"
                id="maxStockQuantity"
                value={productData.maxStockQuantity}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                step="0.01"
              />
            </div>

            <div>
              <label
                htmlFor="initialStockQuantity"
                className="block text-sm text-neutral-700 mb-2"
              >
                Stock Inicial (kg)
              </label>
              <input
                type="number"
                id="initialStockQuantity"
                value={productData.initialStockQuantity}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                step="0.01"
              />
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="mt-8 pt-6 border-t border-neutral-200">
          <h2 className="text-lg mb-4">Informações Adicionais</h2>

          <div className="grid grid-cols-1 gap-6">
            {/* Curing Time moved above */}
            {/* <div>
                <label htmlFor="curingTime" className="block text-sm text-neutral-700 mb-2">
                  Tempo de Cura (dias)
                </label>
                <input
                  type="number"
                  id="curingTime"
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                />
              </div> */}

            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="showAdvancedDetails"
                  checked={showAdvancedDetails}
                  onChange={(e) => setShowAdvancedDetails(e.target.checked)}
                  className="h-4 w-4 text-neutral-900 rounded border-neutral-300"
                />
                <label
                  htmlFor="showAdvancedDetails"
                  className="ml-2 text-sm text-neutral-700"
                >
                  Detalhes Avançados (JSON)
                </label>
              </div>
              {showAdvancedDetails && (
                <textarea
                  id="advancedDetails"
                  rows="8"
                  placeholder='{ "key": "value" }'
                  value={productData.advancedDetails}
                  onChange={handleAdvancedDetailsChange}
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                ></textarea>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons Responsivos */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 sm:justify-end">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-full sm:w-auto px-6 py-2 border border-neutral-200 rounded-lg hover:bg-neutral-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="w-full sm:w-auto px-6 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800"
            disabled={loading}
          >
            {loading ? "Guardando..." : "Guardar Alterações"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateProducts;
